import { SpinnerArea } from 'components/spinner/SpinnerArea';
import { useConfigCatClient } from 'configcat-react';
import { buildConfigCatUser } from 'contexts/configcat/buildConfigCatUser';
import { useRouteEntities } from 'contexts/RouteEntityContext';
import { useUser } from 'contexts/UserContext';
import { ReactNode, useEffect, useMemo } from 'react';
import { sentryCaptureException } from 'utils/sentry';

/**
 * Syncs the app's authenticated user identity and current route context
 * into ConfigCat's default user so that targeting rules based on user ID,
 * email, role, corporation, or location evaluate correctly at runtime.
 *
 * Must be rendered inside both `<UserProvider>` and `<RouteEntityProvider>`
 * (for the context values) and inside `<ConfigCatProvider>` (for the client).
 */
export function ConfigCatUserSync({ children }: { children: ReactNode }) {
    const { user } = useUser();
    const { corporation, location, isLoading } = useRouteEntities();

    const configCatUser = useMemo(
        () => (user ? buildConfigCatUser({ user, corporation, location }) : undefined),
        [user, corporation, location],
    );

    const client = useConfigCatClient();

    useEffect(() => {
        if (!user || !configCatUser) {
            client.clearDefaultUser();
            return;
        }

        if (isLoading) {
            return;
        }

        try {
            client.setDefaultUser(configCatUser);
        } catch (error) {
            sentryCaptureException({
                error,
                tags: { component: 'ConfigCatUserSync' },
                context: {
                    configcat: { userId: user.Id },
                },
            });
        }

        return () => {
            client.clearDefaultUser();
        };
    }, [client, user, isLoading, configCatUser]);

    return (
        <SpinnerArea isLoading={isLoading} hideChildren>
            {children}
        </SpinnerArea>
    );
}
